import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'

import { modalContent } from '../../../shared/subscription-constants'
import { MODALS } from '../../../../constants'
import { STORAGE_KEY_VOUCHER_APPLICABLE_PLANS } from '../../../../storage-keys'
import { usePlanChangeFlag } from '../../../../hooks/usePlanChange'
import Button from '../../../../components/button'
import checkCircleIcon from '../../../../../images/check-circle.svg'
import { subscriptionGa } from '../../../../lib/analytics/ga'
import { sanitizeDom } from '../../../../utils'
import styles from './subscription-selection.css'

/**
 * Subscription Selection component for Add Voucher flow.
 * - Exactly 1 applicable plan: render voucher validated view and continue CTA.
 * - More than 1 applicable plan: redirect to add-subscription modal on home page.
 * - No applicable plan: render nothing.
 */
function SubscriptionSelection({
  voucherDetails,
  onContinueButtonClick
}) {
  const history = useHistory()
  const getPlanDetails = usePlanChangeFlag()

  const applicablePlans = voucherDetails.applicablePlans || []
  const defaultSubscriptions = [
    getPlanDetails.basic,
    getPlanDetails.standard,
    getPlanDetails.annual
  ]
  const subscriptions = defaultSubscriptions.filter(sub => applicablePlans.includes(sub.sku))

  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY_VOUCHER_APPLICABLE_PLANS,
      applicablePlans.toString()
    )
  }, [applicablePlans])

  useEffect(() => {
    if (subscriptions.length > 1) {
      // Exit the add voucher flow and navigate to the add-subscription modal on the home page
      // so the user can choose a plan first, then apply a voucher.
      history.push({
        pathname: '/',
        search: qs.stringify({
          content: modalContent.selection,
          modal: MODALS.qsParams.addSubscription
        })
      })
    }
  }, [history, subscriptions.length])

  if (subscriptions.length !== 1) {
    return null
  }

  const [subscription] = subscriptions
  const voucherDetail = subscription.voucherDetail || {}
  const voucherDescriptions = voucherDetail.desc || []

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>Select your plan - NEON</title>
      </Helmet>

      <img alt="Required" src={checkCircleIcon} />

      <h2 className={styles.neonVoucherValidatedHeader}>{voucherDetail.header}</h2>

      <div className={styles.neonVoucherValidatedCard}>
        <div className={styles.featuresVoucherValidatedHeading}>{voucherDetail.subHeader}</div>
        <ul>
          {
            voucherDescriptions.map(desc => (
              <li key={desc} dangerouslySetInnerHTML={{ __html: sanitizeDom(desc) }} />
            ))
          }
        </ul>
      </div>

      <div className={styles.neonVoucherValidatedSummary}>
        {voucherDetail.summary}
      </div>

      <Button
        data-e2e="voucher-validated-continue-button"
        text="Continue"
        className={styles.continueButton}
        onClick={() => {
          onContinueButtonClick(subscription.id)
          subscriptionGa({ action: 'Continue', label: 'neonVoucher' })
        }}
      />
    </div>
  )
}

SubscriptionSelection.propTypes = {
  voucherDetails: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    amount: PropTypes.string,
    couponType: PropTypes.string,
    planSku: PropTypes.string,
    applicablePlans: PropTypes.arrayOf(PropTypes.string),
    planId: PropTypes.string
  }).isRequired,
  onContinueButtonClick: PropTypes.func.isRequired
}

export default SubscriptionSelection
